import React, { useMemo } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  Icon,
  SimpleGrid,
} from '@chakra-ui/react';
import { FolderOpen, Buildings, ArrowRight } from 'phosphor-react';
import { useNavigate } from 'react-router-dom';
import SecaoClienteLayout from '../layout';
import { formatCNPJ } from '../../../utils/FormatFunctions';

interface IEmpresa {
  id: number;
  external_id: string;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string | null;
}

interface ClienteData {
  id: number;
  guid: string;
  nome: string;
  email: string;
  empresas?: IEmpresa[];
}

const SecaoClienteDossie = () => {
  const navigate = useNavigate();

  // Pegar dados do cliente do localStorage
  const clienteDataStr = localStorage.getItem('cliente_data');
  const clienteData: ClienteData | null = clienteDataStr ? JSON.parse(clienteDataStr) : null;

  const empresasValidadas = useMemo(() => {
    return clienteData?.empresas || [];
  }, [clienteData?.empresas]);

  // Navegar para o dossiê da empresa usando external_id
  const handleEmpresaClick = (empresa: IEmpresa) => {
    // Sempre usar external_id (é o identificador esperado pela API de dossiê)
    navigate(`/secao-cliente/dossie/${empresa.external_id}`);
  };

  return (
    <SecaoClienteLayout>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <HStack spacing={3}>
            <Box p={3} borderRadius="xl" bg="blue.50">
              <Icon as={FolderOpen} boxSize={6} color="blue.500" weight="duotone" />
            </Box>
            <Box>
              <Heading size="lg" color="gray.800">
                Dossiê
              </Heading>
              <Text color="gray.500" fontSize="sm">
                Acesse os documentos e informações das suas empresas
              </Text>
            </Box>
          </HStack>
        </Box>

        {/* Lista de empresas */}
        <Box>
          <HStack mb={4}>
            <Icon as={Buildings} boxSize={5} color="gray.500" />
            <Text fontWeight="600" color="gray.700">
              Selecione uma empresa
            </Text>
          </HStack>
          {empresasValidadas.length > 0 ? (
            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
              {empresasValidadas.map(empresa => (
                <Card
                  key={empresa.id}
                  variant="outline"
                  cursor="pointer"
                  transition="all 0.2s"
                  _hover={{
                    borderColor: 'blue.300',
                    shadow: 'md',
                    transform: 'translateY(-2px)',
                  }}
                  onClick={() => handleEmpresaClick(empresa)}
                >
                  <CardBody>
                    <HStack spacing={3} justify="space-between">
                      <HStack spacing={3} flex={1} minW={0}>
                        <Box p={2} borderRadius="lg" bg="blue.50">
                          <Icon as={Buildings} boxSize={5} color="blue.500" />
                        </Box>
                        <Box flex={1} minW={0}>
                          <Text fontWeight="600" color="gray.800" noOfLines={1}>
                            {empresa.razao_social}
                          </Text>
                          {empresa.cnpj && (
                            <Text fontSize="sm" color="gray.500">
                              {formatCNPJ(empresa.cnpj)}
                            </Text>
                          )}
                        </Box>
                      </HStack>
                      <Icon as={ArrowRight} boxSize={5} color="gray.400" />
                    </HStack>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          ) : (
            <Card variant="outline" bg="gray.50">
              <CardBody>
                <VStack py={8} spacing={4}>
                  <Icon as={Buildings} boxSize={12} color="gray.300" />
                  <Text color="gray.500" textAlign="center">
                    Nenhuma empresa vinculada encontrada
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          )}
        </Box>
      </VStack>
    </SecaoClienteLayout>
  );
};

export default SecaoClienteDossie;
